// Pipeline (kanban by status) logic of the Marketing module, pure.
import type { Identifier } from "ra-core";

import { pieceDate } from "./calendar";
import { CONTENT_STATUSES } from "./constants";
import type { ContentItem, ContentStatus } from "./types";

export type PieceColumns = Record<ContentStatus, ContentItem[]>;

const byIndex = (left: ContentItem, right: ContentItem): number =>
  (left.index ?? 0) - (right.index ?? 0) ||
  (pieceDate(left)?.getTime() ?? Number.MAX_SAFE_INTEGER) -
    (pieceDate(right)?.getTime() ?? Number.MAX_SAFE_INTEGER) ||
  String(left.id).localeCompare(String(right.id), undefined, {
    numeric: true,
  });

/** One column per status, each ordered by `index` (then date). */
export const groupPiecesByStatus = (
  items: readonly ContentItem[],
): PieceColumns => {
  const columns = Object.fromEntries(
    CONTENT_STATUSES.map((status) => [status, [] as ContentItem[]]),
  ) as PieceColumns;
  for (const item of items) (columns[item.status] ?? columns.idea).push(item);
  for (const status of CONTENT_STATUSES) columns[status].sort(byIndex);
  return columns;
};

export interface PiecePatch {
  id: Identifier;
  data: Partial<Pick<ContentItem, "status" | "index" | "published_at">>;
  previousData: ContentItem;
}

/**
 * Moves a piece to `toStatus` at `toIndex` and renumbers the columns it
 * leaves and enters. Only rows whose status / index change get a patch.
 * Moving to "publicado" stamps `published_at` (when empty) with `nowIso`.
 */
export const movePiece = (
  columns: PieceColumns,
  id: Identifier,
  toStatus: ContentStatus,
  toIndex: number,
  nowIso: string,
): PiecePatch[] => {
  const fromStatus = CONTENT_STATUSES.find((status) =>
    columns[status].some((item) => String(item.id) === String(id)),
  );
  if (!fromStatus) return [];
  const moving = columns[fromStatus].find(
    (item) => String(item.id) === String(id),
  )!;
  const source = columns[fromStatus].filter((item) => item !== moving);
  const target = fromStatus === toStatus ? source : [...columns[toStatus]];
  const at = Math.max(0, Math.min(toIndex, target.length));
  target.splice(at, 0, moving);

  const patches: PiecePatch[] = [];
  const renumber = (list: ContentItem[], status: ContentStatus) =>
    list.forEach((item, index) => {
      const data: PiecePatch["data"] = {};
      if (item.status !== status) data.status = status;
      if ((item.index ?? 0) !== index) data.index = index;
      if (data.status === "publicado" && !item.published_at)
        data.published_at = nowIso;
      if (Object.keys(data).length)
        patches.push({ id: item.id, data, previousData: item });
    });
  renumber(target, toStatus);
  if (fromStatus !== toStatus) renumber(source, fromStatus);
  return patches;
};

/** Planned date passed and the piece is still not published. */
export const isOverdue = (item: ContentItem, now: Date): boolean =>
  item.status !== "publicado" &&
  !!item.scheduled_at &&
  (pieceDate(item)?.getTime() ?? Infinity) < now.getTime();

export interface PieceFilter {
  channel?: string | null;
  plan?: string | null;
}

/** Channel / plan filters of the pipeline and calendar ("" = all). */
export const filterPieces = (
  items: readonly ContentItem[],
  { channel, plan }: PieceFilter,
): ContentItem[] =>
  items.filter(
    (item) =>
      (!channel || String(item.channel_id ?? "") === channel) &&
      (!plan || String(item.plan_id ?? "") === plan),
  );

/** Index of a new piece: the end of its column. */
export const nextIndex = (
  items: readonly ContentItem[],
  status: ContentStatus,
): number =>
  items
    .filter((item) => item.status === status)
    .reduce((max, item) => Math.max(max, (item.index ?? 0) + 1), 0);
